/**
 * Per-route <title> + meta description for the homepage and the public
 * marketing pages (#3515).
 *
 * The site is a Vite SPA: every route is served the same index.html, whose
 * head is a generic shell ("ORO move"). The Pages Functions for /, /about,
 * /faq, /leaders and /members (functions/index.ts, functions/about.ts etc.)
 * pass the served HTML through `applyPageMeta`, so each of those routes has
 * its own title and description in the HTML on the wire; every other route
 * keeps the generic shell. The page components read the same
 * `MARKETING_PAGE_META` entry for the client-side tab title
 * (useDocumentTitle), so in-app navigation and the served HTML agree.
 *
 * PURE: no Workers globals (no HTMLRewriter, no PagesFunction), because this
 * module is also imported by the React app and by a vitest test under src/.
 *
 * Browser <title>s carry no ™: the page body renders the brand's single mark
 * (Rule 24), and every existing <title> in the app is the bare "ORO move". The
 * og:/twitter: titles are a separate rendered artifact (the share card), so
 * they carry the single mark, as the shell's "ORO move™" share title does.
 */


export interface PageMeta {
  title: string;
  description: string;
}

export const MARKETING_PAGE_META = {
  '/': {
    title: 'ORO move: Free club management for running and cycling clubs',
    description:
      'Free app for running and cycling clubs: events, RSVPs, check-ins, and chat, so your club just shows up.',
  },
  '/about': {
    title: 'About ORO move: club software for running and cycling clubs',
    description:
      "Why ORO move exists: running and cycling clubs deserve software that cares about them. Meet the founder and see what we're building.",
  },
  '/faq': {
    title: 'ORO move FAQ: pricing, privacy, sign-in and the app',
    description:
      'Is ORO move free? Do you sell my data? Do I need a password? Straight answers about running your club on ORO move.',
  },
  '/leaders': {
    title: 'For club leaders: run your running or cycling club on ORO move',
    description:
      "Running your club shouldn't be painful. Events, RSVPs, announcements, chat and check-ins in one place, and the core app is free.",
  },
  '/members': {
    title: 'ORO move for club members: know where to be, then show up',
    description:
      "Your club uses ORO move to keep the plan together: where to be, when to be there, and what changed. Joining is free, with no password.",
  },
} as const satisfies Record<string, PageMeta>;

export type MarketingPath = keyof typeof MARKETING_PAGE_META;

/** The homepage <title>; functions/index.ts serves it, HomePage re-sets it client-side. */
export const HOME_PAGE_TITLE = MARKETING_PAGE_META['/'].title;

/** Escape for a double-quoted HTML attribute value or element text. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Rewrite one meta tag's content, keyed by its name/property attribute.
 * Matches the tag regardless of attribute order. Returns the html unchanged
 * when the tag is absent; the served-HTML test pins that every tag IS present
 * in index.html, so a template rename fails CI instead of shipping silently.
 */
function setMetaContent(html: string, key: string, value: string): string {
  const keyRe = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const tagRe = new RegExp(`<meta\\b[^>]*\\b(?:name|property)="${keyRe}"[^>]*>`, 'i');
  return html.replace(tagRe, (tag) =>
    tag.replace(/\bcontent="[^"]*"/i, () => `content="${escapeHtml(value)}"`)
  );
}

/** The share-card title: the first brand mention carries the single ™ (Rule 24). */
export function shareTitle(title: string): string {
  return title.replace('ORO move', 'ORO move™');
}

/**
 * Return `html` with the route's title, description, and the matching
 * og:/twitter: title (marked, see shareTitle), description and og:url.
 * og:image is left as served.
 */
export function applyPageMeta(html: string, meta: PageMeta, url: string): string {
  let out = html.replace(/<title>[^<]*<\/title>/i, () => `<title>${escapeHtml(meta.title)}</title>`);
  out = setMetaContent(out, 'description', meta.description);
  out = setMetaContent(out, 'og:title', shareTitle(meta.title));
  out = setMetaContent(out, 'og:description', meta.description);
  out = setMetaContent(out, 'og:url', url);
  out = setMetaContent(out, 'twitter:title', shareTitle(meta.title));
  out = setMetaContent(out, 'twitter:description', meta.description);
  return out;
}
